import { client } from "./client.gen"
import { extractErrorMessage } from "@/utils/errorMessages"
import { notifyGlobal401 } from "@/utils/userSessionCache"
import { maybeAttachCsrfHeader } from "@/utils/csrf"

const apiUrl = import.meta.env?.VITE_API_URL

if (!apiUrl) {
  throw new Error("VITE_API_URL environment variable is not defined")
}

export const API_BASE_URL = apiUrl.replace(/\/$/, "")

/**
 * Normalized API error with status and optional details.
 */
export class ApiError extends Error {
  status: number
  details?: unknown

  constructor(message: string, status: number, details?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.details = details
  }
}

/**
 * Configure the generated SDK client with base URL, credentials, and error normalization.
 */
client.setConfig({
  baseUrl: API_BASE_URL,
  credentials: "include",
  throwOnError: false,
  responseStyle: "fields",
})

client.interceptors.request.use((request) => {
  const headers = new Headers(request.headers)
  maybeAttachCsrfHeader(request.method, headers)

  return new Request(request, { headers })
})

client.interceptors.error.use((error, response) => {
  if (error instanceof ApiError) {
    if (error.status === 401) notifyGlobal401()
    return error
  }

  const status = response?.status ?? 0

  if (status === 401) notifyGlobal401()

  const message = extractErrorMessage(error, status)

  return new ApiError(message, status, error)
})

export { client } from "./client.gen"
